import { useCallback, useEffect, useRef, useState } from 'react'
import type { Task, Section } from '../../../lib/db/models'
import { useSoftAddSound } from '@/src/renderer/lib/sounds'
import { duplicateTask } from '../utils/duplicateTask'
import { useDataSource } from '../../../lib/dataSource'

interface NavItem {
  type: 'task' | 'section'
  id: string
}

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseDuplicateItemsParams {
  segments: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  selectedIds: Set<string>
  flatNavList: NavItem[]
  focusedNavIndex: number | null
  setSelectedIds: (ids: Set<string>) => void
  setFocusedNavIndexWithAnchor: (idx: number | null) => void
}

/**
 * Duplicates tasks and/or sections based on current selection.
 *
 * Selection priority:
 * - Sections take precedence: tasks inside a selected section are NOT individually duplicated
 * - Selected sections are duplicated with all their tasks
 * - Remaining selected tasks (outside selected sections) are duplicated individually
 * - If nothing selected but an item is focused, that item is duplicated
 */
export function useDuplicateItems({
  segments,
  tasks,
  sections,
  selectedIds,
  flatNavList,
  focusedNavIndex,
  setSelectedIds,
  setFocusedNavIndexWithAnchor,
}: UseDuplicateItemsParams) {
  const playSoftAdd = useSoftAddSound()
  const dataSource = useDataSource()

  // Refs for volatile values to avoid re-registering the keyboard handler
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const flatNavListRef = useRef(flatNavList)
  flatNavListRef.current = flatNavList
  const focusedNavIndexRef = useRef(focusedNavIndex)
  focusedNavIndexRef.current = focusedNavIndex
  /*
    State, kein Ref (#209, gemeldet 28.09.2026): Steht die Kopie schon in der Liste, wenn das
    Duplizieren fertig ist — aus der offenen Karte fast immer —, ändert sich `flatNavList` nicht
    mehr, und ein Ref wurde nie abgeholt. Der Fokus blieb am Original, nur die Auswahl sprang.
  */
  const [pendingFocus, setPendingFocus] = useState<string[] | null>(null)
  const pendingScrollSectionRef = useRef<string | null>(null)

  // Focus the first duplicated item once it appears in flatNavList
  // (useLiveQuery updates asynchronously after Dexie writes)
  useEffect(() => {
    if (!pendingFocus) return
    const firstNewIdx = flatNavList.findIndex(item => pendingFocus.includes(item.id))
    if (firstNewIdx !== -1) {
      setFocusedNavIndexWithAnchor(firstNewIdx)
      setPendingFocus(null)

      // Scroll to the first duplicated section if applicable
      const sectionId = pendingScrollSectionRef.current
      if (sectionId) {
        pendingScrollSectionRef.current = null
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-section-id="${sectionId}"]`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        })
      }
    }
  }, [flatNavList, pendingFocus, setFocusedNavIndexWithAnchor])

  const handleDuplicate = useCallback(async (overrideTaskIds?: string[]) => {
    /*
      **Ohne Projekt geht es trotzdem** (Abnahme 21.09.2026, F-KN2). Hier stand
      `if (!projectId) return`, und damit tat Duplizieren in **Kurz notiert**
      gar nichts — weder die Taste noch die Menüzeile. Deren Einträge sind
      Aufgaben OHNE Projekt, ihre Abschnitte bloß Zeitfenster.

      Gebraucht wird die Kennung nur für kopierte **Abschnitte**, und die holt
      sich der Abschnitt weiter unten ohnehin aus sich selbst
      (`section.projectId`). Eine Aufgabenkopie erbt ihren Platz aus der
      Vorlage (`cloneTaskFields`) — ohne Projekt also: ohne Projekt.
    */
    const currentTasks = tasksRef.current
    const currentSections = sectionsRef.current
    const selected = selectedIdsRef.current
    const navList = flatNavListRef.current
    const focusIdx = focusedNavIndexRef.current

    if (!currentTasks || !currentSections) return

    // Determine what to duplicate
    let sectionIdsToDuplicate: string[] = []
    let taskIdsToDuplicate: string[] = []

    if (overrideTaskIds && overrideTaskIds.length > 0) {
      // Explicit override (e.g. from right-click context menu) — bypass selection logic
      taskIdsToDuplicate = [...overrideTaskIds]
    } else if (selected.size > 0) {
      // Separate selected IDs into sections and tasks
      const sectionSet = new Set(currentSections.map(s => s.id))
      for (const id of selected) {
        if (sectionSet.has(id)) {
          sectionIdsToDuplicate.push(id)
        } else {
          taskIdsToDuplicate.push(id)
        }
      }

      // Remove tasks that belong to a selected section (section duplication covers them)
      if (sectionIdsToDuplicate.length > 0) {
        const selectedSectionSet = new Set(sectionIdsToDuplicate)
        taskIdsToDuplicate = taskIdsToDuplicate.filter(taskId => {
          const task = currentTasks.find(t => t.id === taskId)
          return !task?.sectionId || !selectedSectionSet.has(task.sectionId)
        })
      }
    } else if (focusIdx !== null) {
      // Nothing selected — duplicate focused item
      const focusedItem = navList[focusIdx]
      if (!focusedItem) return
      if (focusedItem.type === 'section') {
        sectionIdsToDuplicate = [focusedItem.id]
      } else {
        taskIdsToDuplicate = [focusedItem.id]
      }
    } else {
      return // Nothing to duplicate
    }

    if (sectionIdsToDuplicate.length === 0 && taskIdsToDuplicate.length === 0) return

    // Build the full interleaved item list for order computation
    const allItems = [
      ...currentTasks.map(t => ({ type: 'task' as const, id: t.id, order: t.order, sectionId: t.sectionId })),
      ...currentSections.map(s => ({ type: 'section' as const, id: s.id, order: s.order, sectionId: null as string | null })),
    ].sort((a, b) => a.order - b.order)

    const newIds: string[] = []
    let firstNewSectionId: string | null = null

    // ── Duplicate sections (process from bottom to top for stable order) ──
    const orderedSectionIds = sectionIdsToDuplicate
      .map(id => ({ id, order: currentSections.find(s => s.id === id)?.order ?? 0 }))
      .sort((a, b) => b.order - a.order) // bottom first

    for (const { id: sectionId } of orderedSectionIds) {
      const section = currentSections.find(s => s.id === sectionId)
      if (!section) continue

      // Find all tasks in this section
      const sectionTasks = currentTasks
        .filter(t => t.sectionId === sectionId)
        .sort((a, b) => a.order - b.order)

      // Find the order range end: last item in this section
      const lastSectionItem = sectionTasks.length > 0
        ? sectionTasks[sectionTasks.length - 1]
        : section
      const lastOrder = lastSectionItem.order

      // Find the next item after this section's range
      const nextItem = allItems.find(item => item.order > lastOrder)
      const gapEnd = nextItem ? nextItem.order : lastOrder + sectionTasks.length + 2

      // Compute orders for the duplicated section + its tasks
      const totalNewItems = 1 + sectionTasks.length
      const gapSize = gapEnd - lastOrder
      const step = gapSize / (totalNewItems + 1)

      // Create new section
      const newSection = await dataSource.createSection({
        title: section.title,
        projectId: section.projectId,
        order: lastOrder + step,
        collapsed: section.collapsed,
        // Noch nicht angezeigt (#192), aber die Kopie soll dem Original gleichen.
        description: section.description ?? null,
      })
      newIds.push(newSection.id)
      // Bottom-first processing → last assigned = topmost duplicate
      firstNewSectionId = newSection.id

      // Create duplicated tasks with new sectionId
      for (let i = 0; i < sectionTasks.length; i++) {
        const task = sectionTasks[i]
        const newTask = await duplicateTask(dataSource, task, {
          sectionId: newSection.id,
          order: lastOrder + step * (i + 2),
        })
        newIds.push(newTask.id)
      }

      // Update allItems for subsequent iterations
      allItems.push(
        { type: 'section', id: newSection.id, order: newSection.order, sectionId: null },
        ...sectionTasks.map((_, i) => ({
          type: 'task' as const,
          id: `temp-${i}`,
          order: lastOrder + step * (i + 2),
          sectionId: newSection.id,
        }))
      )
      allItems.sort((a, b) => a.order - b.order)
    }

    // ── Duplicate individual tasks (process from bottom to top) ──
    const orderedTaskIds = taskIdsToDuplicate
      .map(id => ({ id, order: currentTasks.find(t => t.id === id)?.order ?? 0 }))
      .sort((a, b) => b.order - a.order) // bottom first

    for (const { id: taskId } of orderedTaskIds) {
      const task = currentTasks.find(t => t.id === taskId)
      if (!task) continue

      // Find the next item after this task
      const taskIdx = allItems.findIndex(item => item.id === taskId)
      const nextItem = taskIdx !== -1 && taskIdx < allItems.length - 1
        ? allItems[taskIdx + 1]
        : null
      const newOrder = nextItem
        ? (task.order + nextItem.order) / 2
        : task.order + 1

      const newTask = await duplicateTask(dataSource, task, { order: newOrder })
      newIds.push(newTask.id)

      // Update allItems for subsequent iterations
      allItems.push({ type: 'task', id: newTask.id, order: newOrder, sectionId: task.sectionId })
      allItems.sort((a, b) => a.order - b.order)
    }

    // Select all duplicated items and schedule focus update
    setSelectedIds(new Set(newIds))
    pendingScrollSectionRef.current = firstNewSectionId
    setPendingFocus(newIds)

    newIds.forEach((_, i) => setTimeout(() => playSoftAdd(), i * 80))
  }, [setSelectedIds, playSoftAdd, dataSource])

  return { handleDuplicate }
}
